/* eslint-disable max-lines */
import { CardWithIntent, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type ReactNode } from "react";

import { Button } from "../../../components/Button";
import { PLACEMENT_ACTION_TYPE, type PlacementActionType } from "./utils/getPrimaryAction";

export interface PrimaryActionProps {
  type: PlacementActionType;
  label: string;
  warning?: string;
  description?: string;
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  startIcon?: string;
  additionalContent?: React.ReactNode;
}

interface ActionButtonWithDescriptionProps {
  description?: string;
  additionalContent?: ReactNode;
  isLoading?: boolean;
  buttonProps: {
    label: string;
    onClick: () => void;
    disabled: boolean;
    variant?: "contained" | "outlined";
    startIcon?: string;
  };
}

function ActionButtonWithDescription(props: ActionButtonWithDescriptionProps) {
  const { description, additionalContent, isLoading = false, buttonProps } = props;

  return (
    <Stack spacing={5}>
      {description && (
        <Text
          variant="body2"
          align="center"
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          {description}
        </Text>
      )}
      <LoadingButton
        fullWidth
        variant={buttonProps.variant ?? "contained"}
        size="large"
        disabled={buttonProps.disabled}
        isLoading={isLoading}
        startIconType={buttonProps.startIcon as "arrow-up-right-from-square" | undefined}
        onClick={buttonProps.onClick}
      >
        {buttonProps.label}
      </LoadingButton>
      {additionalContent}
    </Stack>
  );
}

// eslint-disable-next-line complexity
export function PlacementPrimaryAction(props: PrimaryActionProps) {
  const {
    type,
    label,
    warning,
    description,
    onClick,
    disabled,
    isLoading,
    startIcon,
    additionalContent,
  } = props;

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (type) {
    case PLACEMENT_ACTION_TYPE.ADD_QUALIFICATION: {
      return (
        <>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={onClick}
          >
            {label}
          </LoadingButton>
          {description && (
            <Text variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {description}
            </Text>
          )}
        </>
      );
    }

    case PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE: {
      return (
        <>
          {warning && (
            <Text variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {warning}
            </Text>
          )}
          <Button fullWidth variant="contained" size="large" onClick={onClick}>
            {label}
          </Button>
        </>
      );
    }

    case PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT: {
      return (
        <>
          {warning && (
            <Text variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {warning}
            </Text>
          )}
          <Button fullWidth variant="contained" size="large" disabled={disabled} onClick={onClick}>
            {label}
          </Button>
        </>
      );
    }

    case PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL: {
      return (
        <ActionButtonWithDescription
          buttonProps={{
            label,
            onClick,
            disabled,
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.COMPLETE_PROFILE: {
      if (additionalContent) {
        return (
          <CardWithIntent intent="neutral" sx={{ padding: 6 }}>
            <Stack direction="row" spacing={6} alignItems="center">
              {additionalContent}
              <Button
                variant="outlined"
                sx={{ width: "128px", flexBasis: "128px" }}
                onClick={onClick}
              >
                {label}
              </Button>
            </Stack>
          </CardWithIntent>
        );
      }

      return (
        <Button fullWidth variant="contained" size="large" onClick={onClick}>
          {label}
        </Button>
      );
    }

    case PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER:
    case PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER_NEXT_STEPS: {
      return (
        <ActionButtonWithDescription
          description={description}
          buttonProps={{
            label,
            onClick,
            disabled,
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.VERIFY_ATTENDANCE: {
      return (
        <Button fullWidth variant="contained" size="large" disabled={disabled} onClick={onClick}>
          {label}
        </Button>
      );
    }

    case PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW: {
      return (
        <ActionButtonWithDescription
          additionalContent={additionalContent}
          isLoading={isLoading}
          buttonProps={{
            label,
            onClick,
            disabled,
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.APPLY: {
      return (
        <Stack spacing={5}>
          {warning && (
            <Text variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {warning}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoading}
            disabled={disabled}
            onClick={onClick}
          >
            {label}
          </LoadingButton>
        </Stack>
      );
    }

    case PLACEMENT_ACTION_TYPE.LATE_CANCEL:
    case PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW: {
      return (
        <ActionButtonWithDescription
          buttonProps={{
            label,
            onClick,
            disabled,
            variant: "outlined",
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.APPLY_VIA_WEBLINK: {
      return (
        <ActionButtonWithDescription
          isLoading={isLoading}
          buttonProps={{
            label,
            onClick,
            disabled,
            startIcon,
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.UPDATE_ETA: {
      return (
        <ActionButtonWithDescription
          buttonProps={{
            label,
            onClick,
            disabled,
          }}
        />
      );
    }

    case PLACEMENT_ACTION_TYPE.HAS_CONNECTED_LISTING_INTERVIEW: {
      return (
        <>
          {warning && (
            <Text variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {warning}
            </Text>
          )}
          <ActionButtonWithDescription
            buttonProps={{
              label,
              onClick,
              disabled,
            }}
          />
        </>
      );
    }

    default: {
      return null;
    }
  }
}
/* eslint-enable max-lines */
